<script setup lang="ts">
import { RouterView } from "vue-router";
import LoadingBar from "@/components/LoadingBar.vue";
import { computed, ref, watch } from "vue";
import { appName } from "@/globalConfig";
import NotificationManager from "./components/notifications/NotificationManager.vue";
import router from "./router";
import SideBar from "@/components/navigation/SideBar.vue";
import { isAuthPluginInstalled, showProviderSelectionModal, useAuth } from "@/auth";
import SelectionModal from "@/components/authProvider/SelectionModal.vue";
import { usePwaUpdate } from "@/composables/usePwaUpdate";
import { useNotificationStore } from "@/stores/notification";

// Only use the configured OIDC manager if a provider was actually installed
// at boot. Otherwise treat the app as unauthenticated.
const auth = isAuthPluginInstalled.value ? useAuth() : null;
const isAuthenticated = computed(() => auth?.isAuthenticated.value);
const sidebarOpen = ref(false);
const { needRefresh, reload } = usePwaUpdate();
const notificationStore = useNotificationStore();

watch(needRefresh, (refreshNeeded) => {
    if (!refreshNeeded) return;
    notificationStore.addNotification({
        title: "Update available",
        description: "A new version is available. Save your work, then reload to update.",
        state: "info",
        persist: true,
        action: { label: "Reload", onClick: reload },
    });
});

const routeKey = computed(() => {
    let routeKey = router.currentRoute.value.fullPath;

    // Check if the route is an overview route, and return a unique route key. This will disable component reuse for dynamic routes and allow the component to reload data
    if (routeKey.includes("tag/overview/") || routeKey.includes("post/overview/")) {
        return routeKey;
    }

    // Disable the route key for all other routes. This will enable component reuse for dynamic routes and prevent the component from reloading data
    return "";
});
</script>

<template>
    <template v-if="isAuthenticated">
        <!-- The sidebar owns its own width (and animates it on collapse); the grid column just
        tracks it via `auto`, so the two never drift out of sync. -->
        <div class="grid h-screen overflow-hidden lg:grid-cols-[auto_1fr]">
            <SideBar v-model:open="sidebarOpen" />

            <div class="min-h-0 min-w-0 overflow-hidden">
                <!-- The routeKey disables component reuse in cases where data needs to be reloaded for dynamic
                routes (e.g. Post / Tag overviews) -->
                <RouterView :key="routeKey" v-slot="{ Component }">
                    <component :is="Component" :onOpenMobileSidebar="() => (sidebarOpen = true)" />
                </RouterView>
            </div>
        </div>
    </template>

    <div v-else class="absolute flex h-full w-full items-center justify-center">
        <div class="flex flex-col items-center gap-4">
            <img class="w-72" src="@/assets/logo.svg" :alt="appName" />
            <div class="flex items-center gap-2 text-lg"><LoadingBar /></div>
        </div>
    </div>

    <Teleport to="body">
        <NotificationManager />
        <SelectionModal v-model:isVisible="showProviderSelectionModal" />
    </Teleport>
</template>
